/* Tool-agnostic analytics facade with consent gating and provider adapters */

type ConsentCategory = 'essential' | 'analytics' | 'marketing' | 'performance';

type ConsentState = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  performance: boolean;
};

type BaseEvent = {
  type: 'track' | 'pageview' | 'identify';
  category: ConsentCategory;
};

type TrackEvent = BaseEvent & {
  type: 'track';
  name: string;
  payload?: Record<string, unknown>;
};

type PageviewEvent = BaseEvent & {
  type: 'pageview';
  url: string;
  title?: string;
  payload?: Record<string, unknown>;
};

type IdentifyEvent = BaseEvent & {
  type: 'identify';
  user: Record<string, unknown>;
  traits?: Record<string, unknown>;
};

type PendingEvent = TrackEvent | PageviewEvent | IdentifyEvent;

type AnalyticsHandler = {
  track?: (event: TrackEvent) => void;
  pageview?: (event: PageviewEvent) => void;
  identify?: (event: IdentifyEvent) => void;
  onConsent?: (consent: ConsentState) => void;
};

let handlers: AnalyticsHandler[] = [];
let currentConsent: ConsentState | null = null;
let consentFinalized = false;
const pending: PendingEvent[] = [];

// SSR guard
const isBrowser = () => typeof window !== 'undefined';

const isAllowed = (category: ConsentCategory) => {
  if (category === 'essential') return true;
  if (!consentFinalized || !currentConsent) return false;
  return currentConsent[category];
};

const runHandlers = (event: PendingEvent) => {
  handlers.forEach((handler) => {
    switch (event.type) {
      case 'track':
        handler.track?.(event);
        break;
      case 'pageview':
        handler.pageview?.(event);
        break;
      case 'identify':
        handler.identify?.(event);
        break;
    }
  });
};

const flush = () => {
  if (!consentFinalized) return;
  pending.splice(0).forEach((event) => {
    if (isAllowed(event.category)) {
      runHandlers(event);
    }
  });
};

export const registerAnalytics = (newHandlers: AnalyticsHandler[]) => {
  handlers = newHandlers;
};

export const setConsent = (consent: ConsentState) => {
  currentConsent = { ...consent, essential: true };
  consentFinalized = true;
  handlers.forEach((handler) =>
    handler.onConsent?.(currentConsent as ConsentState),
  );
  flush();
};

export const getConsent = () => currentConsent;

export const track = (
  name: string,
  payload?: Record<string, unknown>,
  category: ConsentCategory = 'analytics',
) => {
  if (!isBrowser()) return;
  const event: TrackEvent = { type: 'track', name, payload, category };
  if (!isAllowed(category)) {
    pending.push(event);
    return;
  }
  runHandlers(event);
};

export const pageview = (
  url: string,
  title?: string,
  payload?: Record<string, unknown>,
  category: ConsentCategory = 'analytics',
) => {
  if (!isBrowser()) return;
  const event: PageviewEvent = {
    type: 'pageview',
    url,
    title,
    payload,
    category,
  };
  if (!isAllowed(category)) {
    pending.push(event);
    return;
  }
  runHandlers(event);
};

export const identify = (
  user: Record<string, unknown>,
  traits?: Record<string, unknown>,
  category: ConsentCategory = 'analytics',
) => {
  if (!isBrowser()) return;
  const event: IdentifyEvent = { type: 'identify', user, traits, category };
  if (!isAllowed(category)) {
    pending.push(event);
    return;
  }
  runHandlers(event);
};

// Optional: simple no-op provider for safety
export const NoopProvider = {
  init: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  identify: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  track: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  pageview: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  setConsent: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
  flush: () => {}, // eslint-disable-line @typescript-eslint/no-empty-function
};

// Helper to read consent cookies if you want to bootstrap from existing cookie values
export const readConsentFromCookies = (prefix: string): ConsentState | null => {
  if (!isBrowser()) return null;
  const read = (key: string) =>
    document.cookie
      ?.split('; ')
      ?.find((entry) => entry.startsWith(`${key}=`))
      ?.split('=')[1];

  const analytics = read(`${prefix}-gatsby-consent-google-analytics`);
  const marketing = read(`${prefix}-gatsby-consent-marketing`);
  const performance = read(`${prefix}-gatsby-consent-performance`);

  if ([analytics, marketing, performance].every((value) => value == null))
    return null;

  return {
    essential: true,
    analytics: analytics === 'true',
    marketing: marketing === 'true',
    performance: performance === 'true',
  };
};
